
import { Options, prop, Vue } from "vue-class-component";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import {
  formatDate,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { ClientRateHistoryApiRequest } from "@/data/payload/api/ClientRegistrationApiRequest";
import DateRangePicker from "@/app/ui/components/date-range-picker/index.vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import PaginationV3 from "@/app/ui/components/pagination-v3/index.vue";
import EmptyList from "@/app/ui/components/empty-list/index.vue";
import { ClientRegistrationController } from "@/app/ui/controllers/ClientRegistrationController";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";
import {
  PRICE_TYPE_CLIENT_RATE_HISTORY,
  REQUEST_TYPE_CLIENT_RATE_HISTORY,
  STATUS_CLIENT_RATE_HISTORY
} from "../constant";
import { OptionsClass } from "@/domain/entities/MainApp";
import { ClientRegistrationDetail } from "@/domain/entities/ClientRegistration";
import { DetailClientManagement } from "@/domain/entities/ClientManagement";

class Props {
  detailData = prop<ClientRegistrationDetail | DetailClientManagement>({
    type: Object,
    default: () => new ClientRegistrationDetail()
  });
}

@Options({
  components: {
    Accordion,
    AccordionTab,
    DateRangePicker,
    PaginationV3,
    EmptyList
  }
})
class PriceHistoryComponent extends Vue.with(Props) {
  formatDate = formatDate;
  REQUEST_TYPE_CLIENT_RATE_HISTORY = REQUEST_TYPE_CLIENT_RATE_HISTORY;
  PRICE_TYPE_CLIENT_RATE_HISTORY = PRICE_TYPE_CLIENT_RATE_HISTORY;
  STATUS_CLIENT_RATE_HISTORY = STATUS_CLIENT_RATE_HISTORY;

  maxScrollHeight = 300;
  scrollResizeObserver: any = null;

  activeIndexMenu: number[] = [];
  isOpen(index: number) {
    return this.activeIndexMenu.includes(index);
  }

  calculateMaxHeight() {
    const cardRef: any = this.$refs.cardRef;
    if (!cardRef?.$el) return;

    const rect = cardRef.$el.getBoundingClientRect();
    const availableHeight = window.innerHeight - rect.top - 100; // 100px for padding and pagination
    this.maxScrollHeight = Math.max(availableHeight, 200); // minimum 200px
  }

  handleResize() {
    this.calculateMaxHeight();
  }

  togglePanel(event: any) {
    const refs: any = this.$refs;
    refs["priceHistoryFilterPanel"]?.toggle(event);
  }

  get statusOptions() {
    return Object.entries(STATUS_CLIENT_RATE_HISTORY).map(
      ([key, value]) =>
        new OptionsClass({
          name: value.label,
          value: key
        })
    );
  }
  get typeOptions() {
    return Object.entries(PRICE_TYPE_CLIENT_RATE_HISTORY).map(
      ([key, value]) =>
        new OptionsClass({
          name: value,
          value: key
        })
    );
  }
  getStatus(status: string) {
    return STATUS_CLIENT_RATE_HISTORY[
      status as keyof typeof STATUS_CLIENT_RATE_HISTORY
    ];
  }

  safeRequestTypeLabel(key: string) {
    const map = REQUEST_TYPE_CLIENT_RATE_HISTORY as Record<string, string>;
    return map[key] ?? key ?? "-";
  }

  get disabledFilter() {
    const { status, type } = this.filter;
    return !(status as OptionsClass)?.value && !(type as OptionsClass)?.value;
  }

  onResetFilter() {
    this.filter = new ClientRateHistoryApiRequest({
      date: this.filter.date
    });
    this.getList();
  }

  onApplyFilter() {
    const refs: any = this.$refs;
    refs["priceHistoryFilterPanel"]?.hide();

    this.filter.pagination.page = 1;
    this.getList();
  }

  toPage(page: number) {
    this.filter.pagination.page = page;
    this.getList();
  }

  filter = new ClientRateHistoryApiRequest();
  listData = new ResponsePayloadV2();

  async getList() {
    try {
      MainAppController.closeMessageModal();
      MainAppController.showLoading();
      this.listData = new ResponsePayloadV2();

      const res = await ClientRegistrationController.getClientRateHistory(
        new ClientRateHistoryApiRequest({
          ...this.filter,
          clientId: this.detailData.id || Number(this.$route.params.id)
        })
      );
      this.listData = res;
      this.filter.pagination = res.pagination;
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t('clientManagement.clientRateHistory.failHistory').toString(),
          this.getList
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  get loading() {
    return MainAppController.loadingModal;
  }

  mounted() {
    this.filter = new ClientRateHistoryApiRequest();
    this.listData = new ResponsePayloadV2();
    this.getList();

    this.$nextTick(() => {
      this.calculateMaxHeight();
      window.addEventListener("resize", this.handleResize);

      if ((window as any).ResizeObserver) {
        const cardRef: any = this.$refs.cardRef;
        if (cardRef?.$el) {
          this.scrollResizeObserver = new (window as any).ResizeObserver(() => {
            this.calculateMaxHeight();
          });
          this.scrollResizeObserver.observe(cardRef.$el);
        }
      }
    });
  }

  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    if (this.scrollResizeObserver) {
      this.scrollResizeObserver.disconnect();
    }
  }
}

export default PriceHistoryComponent;
